//@ts-nocheck
import React, { useState } from "react";
import dayjs from "dayjs";
import Resolve from "lib/Resolve";
import { requestAccessPolicy } from "lib/actions/storages";
import { showPopupFlag } from "lib/actions/userInterface";
import API from "lib/api/accessControl";
import AccessScopes from "lib/constants/AccessScopes";
import store from "store";
import { DangerButton, PrimaryButton, SecondaryButton } from "components/Buttons";
import { DataGrid, DataItem } from "components/DataGrid";
import { CheckboxInput } from "components/Input";
import RadioInput from "components/Input/RadioInput";
import { Paragraph, Text } from "components/Typography";
import FieldGroup from "components/form/FieldGroup/FieldGroup";
import SelectAccessScope from "components/inputs/SelectAccessScope";
import { Modal, ModalContent, ModalFooter, ModalHeader } from "components/modals/Modal";
import { VSpace } from "components/shared";
import { drawerAccessWarnings } from "components/shared/features/storages/utils/drawerAccess.utility";
import style from "../CreateAccessRuleModal/modal.module.scss";
import parentStyle from "../RootModal/modal.module.scss";

const AccessModalRenderScope = ({ rule }) => {
  switch (rule.scope) {
    case AccessScopes.GROUP:
      return <Text className="capitalise">Group: {rule.valueString}</Text>;
    case AccessScopes.ROLE:
      return <Text>Role: {Resolve.resolveRoleName(rule.valueInt)}</Text>;
    case AccessScopes.PROJECT:
      return <Text>Project {Resolve.resolveProjectName(rule.valueInt)}</Text>;
    case AccessScopes.USER:
      return <Text>User: {Resolve.resolveUserFullName(rule.valueInt)}</Text>;
    case AccessScopes.KEYCARD:
      return <Text>Keycard: {rule.valueString}</Text>;
    default:
      return null;
  }
};

const ViewAccessRuleModal = ({ modal, onClose }) => {
  const { storage, policy, grant } = modal.props;

  // A rule created before this feature existed has no allDrawers field in its JSON at all —
  // treat that as unrestricted (true), never as false, or every existing rule would render (and
  // could persist) as "No drawers".
  const initialDrawerAccess = {
    allDrawers: grant.allDrawers ?? true,
    allowedDrawers: grant.allowedDrawers ?? [],
  };
  const [drawerAccess, setDrawerAccess] = useState(initialDrawerAccess);
  const [saving, setSaving] = useState(false);

  // Compared by value, not identity: the checkboxes rebuild the array on every click, and drawer
  // order carries no meaning.
  const sameDrawers = (a, b) =>
    a.allDrawers === b.allDrawers &&
    a.allowedDrawers.length === b.allowedDrawers.length &&
    [...a.allowedDrawers].sort().every((value, i) => value === [...b.allowedDrawers].sort()[i]);
  const hasUnsavedChanges = !sameDrawers(drawerAccess, initialDrawerAccess);

  const handleRemove = async () => {
    try {
      await API.removePolicyGrant(policy.id, grant.id);
      store.dispatch(requestAccessPolicy(policy.id));
      store.dispatch(
        showPopupFlag({
          appearance: "success",
          title: "Access revoked",
        }),
      );
      onClose();
    } catch (err) {
      console.error(err);
      store.dispatch(
        showPopupFlag({
          appearance: "error",
          title: "Failed to revoke access",
        }),
      );
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const newPolicy = {
        ...policy,
        grants: policy.grants.map((existingGrant) =>
          existingGrant.id === grant.id ? { ...existingGrant, ...drawerAccess } : existingGrant,
        ),
      };
      await API.updatePolicy(policy.id, newPolicy);
      store.dispatch(requestAccessPolicy(policy.id));
      store.dispatch(
        showPopupFlag({
          appearance: "success",
          title: "Drawer access updated",
        }),
      );
      onClose();
    } catch (err) {
      console.error(err);
      store.dispatch(
        showPopupFlag({
          appearance: "error",
          title: "Failed to update drawer access",
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAllDrawersChange = (option) => {
    // RadioOption reports a click on the already-selected option as a deselect (null). Drawer
    // access is a two-state choice with no legal third state, so there is nothing to apply:
    // reading null as "all drawers" would wipe the restriction and persist it immediately, with
    // no confirmation and no undo.
    if (!option) {
      return;
    }
    const allDrawers = option.value;
    setDrawerAccess({ allDrawers, allowedDrawers: allDrawers ? [] : drawerAccess.allowedDrawers });
  };

  const handleDrawerToggle = (drawerValue) => {
    const allowedDrawers = drawerAccess.allowedDrawers.includes(drawerValue)
      ? drawerAccess.allowedDrawers.filter((value) => value !== drawerValue)
      : [...drawerAccess.allowedDrawers, drawerValue];
    setDrawerAccess({ allDrawers: false, allowedDrawers });
  };

  const renderDrawerAccess = () => {
    if (!storage?.config?.maglockEnabled) {
      return null;
    }

    const warnings = drawerAccessWarnings(drawerAccess.allDrawers, drawerAccess.allowedDrawers, storage.drawers);

    return (
      <FieldGroup label="Drawers allowed">
        <RadioInput
          options={[
            { value: true, name: "All drawers" },
            { value: false, name: "Selected drawers" },
          ]}
          value={drawerAccess.allDrawers}
          onChange={handleAllDrawersChange}
        />
        {drawerAccess.allDrawers === false && (
          <VSpace gap="gap-2">
            {storage.drawers.map((drawer) => (
              <CheckboxInput
                key={drawer.id}
                input={{ name: `drawer-${drawer.value}` }}
                label={`Drawer ${drawer.value}${drawer.name ? ` — ${drawer.name}` : ""}`}
                value={drawerAccess.allowedDrawers.includes(drawer.value)}
                onChange={() => handleDrawerToggle(drawer.value)}
              />
            ))}
          </VSpace>
        )}
        {warnings.map((warning) => (
          <Paragraph key={warning} className="text-red-500">
            {warning}
          </Paragraph>
        ))}
      </FieldGroup>
    );
  };

  return (
    <Modal className={parentStyle.wideModal}>
      <ModalHeader title="Grant access" />
      <ModalContent>
        <VSpace>
          <FieldGroup label="Access scope">
            <SelectAccessScope name="scope" value={grant.rule.scope} disabled={true} />
            <br />
            <AccessModalRenderScope rule={grant.rule} />
          </FieldGroup>
          {grant.rule.scope !== AccessScopes.KEYCARD && (
            <FieldGroup label="Methods allowed">
              <Paragraph className="text-muted">Select which access methods will be active for the rule</Paragraph>
              <div className={style.methods}>
                <RadioInput options={[{ value: true, name: "Keycard" }]} value={grant.allowKeycard} />
                <RadioInput options={[{ value: true, name: "Mobile app" }]} value={grant.allowMobileApp} />
              </div>
            </FieldGroup>
          )}
          {renderDrawerAccess()}
          <FieldGroup label="Access period">
            <DataGrid>
              <DataItem label="Active from">
                <Text>
                  {grant.activeDate !== "0001-01-01T00:00:00Z"
                    ? dayjs(grant.activeDate).format("dddd, MMMM D, YYYY h:mm A")
                    : "Not specified"}
                </Text>
              </DataItem>
              <DataItem label="Expire On">
                <Text>
                  {grant.expireDate !== "0001-01-01T00:00:00Z"
                    ? dayjs(grant.expireDate).format("dddd, MMMM D, YYYY h:mm A")
                    : "No expiry"}
                </Text>
              </DataItem>
            </DataGrid>
          </FieldGroup>
        </VSpace>
      </ModalContent>
      <ModalFooter>
        {storage?.config?.maglockEnabled && (
          <PrimaryButton disabled={!hasUnsavedChanges || saving} onClick={handleSave}>
            {saving ? "Saving..." : "Save changes"}
          </PrimaryButton>
        )}
        <DangerButton onClick={handleRemove}>Revoke Access</DangerButton>
        <SecondaryButton onClick={onClose}>Close</SecondaryButton>
      </ModalFooter>
    </Modal>
  );
};

export default ViewAccessRuleModal;
